import React, { useMemo, useState } from "react";
import { formatCurrency, formatDueDate } from "./revenueSummaryUtils";

const partnerKey = (row) => row.partnerId || row.partnerName || "unknown";

const normalizeInvoice = (row) => ({
	invoiceMonth: row.invoiceMonth,
	invoiceNumber: row.invoiceNumber,
	dueDate: row.dueDate,
	overdueDays: row.overdueDays ?? 0,
	pendingNative: row.pendingNative ?? 0,
	currency: row.currency || "USD",
});

const groupOverdueByPartner = (rows) => {
	const map = new Map();

	for (const row of rows) {
		const key = partnerKey(row);
		if (!map.has(key)) {
			map.set(key, {
				partnerId: row.partnerId,
				partnerName: row.partnerName || "—",
				currency: row.currency || "USD",
				invoices: [],
			});
		}

		const group = map.get(key);
		if (row.currency) group.currency = row.currency;

		if (Array.isArray(row.invoices) && row.invoices.length > 0) {
			row.invoices.forEach((inv) => {
				group.invoices.push(normalizeInvoice({ ...inv, currency: inv.currency || row.currency }));
			});
		} else if (row.invoiceMonth != null || row.pendingNative != null || row.dueDate) {
			group.invoices.push(normalizeInvoice(row));
		}
	}

	return Array.from(map.values())
		.map((group) => {
			const totalPending = group.invoices.reduce((sum, inv) => sum + (inv.pendingNative ?? 0), 0);
			const maxOverdueDays = group.invoices.reduce(
				(max, inv) => Math.max(max, inv.overdueDays ?? 0),
				0
			);
			return {
				...group,
				totalPending,
				maxOverdueDays,
				invoiceCount: group.invoices.length,
			};
		})
		.sort((a, b) => b.maxOverdueDays - a.maxOverdueDays || b.totalPending - a.totalPending);
};

const OverduePartnerRow = ({ partner, expanded, onToggle }) => {
	const hasInvoices = partner.invoiceCount > 0;

	return (
		<>
			<tr
				className={`rs-overdue-partner-row ${hasInvoices ? "rs-overdue-partner-row--clickable" : ""} ${expanded ? "rs-overdue-partner-row--expanded" : ""}`}
				onClick={() => hasInvoices && onToggle()}
			>
				<td className="rs-overdue-partner-cell">
					<div className="rs-overdue-partner-name">
						{hasInvoices && (
							<span className="rs-expand-icon">{expanded ? "▼" : "▶"}</span>
						)}
						{partner.partnerName}
					</div>
					{partner.invoiceCount > 1 && (
						<div className="rs-overdue-partner-meta">{partner.invoiceCount} invoices</div>
					)}
				</td>
				<td className="rs-overdue-days">{partner.maxOverdueDays}d</td>
				<td className="rs-text-right rs-text-bold">
					{formatCurrency(partner.totalPending, partner.currency)}
				</td>
			</tr>

			{expanded && partner.invoices.map((inv, idx) => (
				<tr key={`${partner.partnerId || partner.partnerName}-${inv.invoiceMonth}-${idx}`} className="rs-overdue-invoice-row">
					<td className="rs-overdue-invoice-cell">
						<div>{inv.invoiceMonth || "—"}</div>
						{inv.invoiceNumber && (
							<div className="rs-overdue-invoice-num">{inv.invoiceNumber}</div>
						)}
					</td>
					<td className="rs-overdue-invoice-sub">
						<div>{formatDueDate(inv.dueDate)}</div>
						<div className="rs-overdue-invoice-days">{inv.overdueDays ?? 0}d overdue</div>
					</td>
					<td className="rs-text-right rs-overdue-invoice-sub">
						{formatCurrency(inv.pendingNative, inv.currency || partner.currency)}
					</td>
				</tr>
			))}
		</>
	);
};

const OverdueCard = ({ overdue }) => {
	const [sectionExpanded, setSectionExpanded] = useState(false);
	const [expandedPartners, setExpandedPartners] = useState({});

	const partnerGroups = useMemo(
		() => groupOverdueByPartner(overdue?.byPartner || []),
		[overdue?.byPartner]
	);

	if (!overdue) return null;

	const totalINR = overdue.totalINR ?? 0;
	const totalUSD = overdue.totalUSD ?? 0;

	if (totalINR <= 0 && totalUSD <= 0) return null;

	const togglePartner = (key) => {
		setExpandedPartners((prev) => ({ ...prev, [key]: !prev[key] }));
	};

	return (
		<div className="rs-overdue">
			<div className="rs-overdue-header">
				<h3 className="rs-overdue-title">Overdue Invoices</h3>
				<button
					type="button"
					onClick={() => setSectionExpanded((v) => !v)}
					className="rs-overdue-toggle"
				>
					{sectionExpanded ? "Hide" : "Show"} ({partnerGroups.length} partner{partnerGroups.length === 1 ? "" : "s"})
				</button>
			</div>

			<div className={`rs-overdue-totals ${sectionExpanded ? "rs-overdue-totals--mb" : ""}`}>
				{totalINR > 0 && (
					<div className="rs-overdue-amount">{formatCurrency(totalINR, "INR")} overdue</div>
				)}
				{totalUSD > 0 && (
					<div className="rs-overdue-amount">{formatCurrency(totalUSD, "USD")} overdue</div>
				)}
			</div>

			{sectionExpanded && partnerGroups.length > 0 && (
				<div className="table-container-v4">
					<table className="data-table-v4 rs-overdue-table">
						<thead>
							<tr>
								<th>Partner / Invoice</th>
								<th className="rs-text-right">Days Overdue</th>
								<th className="rs-text-right">Pending</th>
							</tr>
						</thead>
						<tbody>
							{partnerGroups.map((partner) => {
								const key = partnerKey(partner);
								return (
									<OverduePartnerRow
										key={key}
										partner={partner}
										expanded={!!expandedPartners[key]}
										onToggle={() => togglePartner(key)}
									/>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};

export default OverdueCard;
